import { __awaiter, __generator } from "tslib";
import React, { useEffect, useState, useRef } from "react";
import { VeltContext } from "../../context/SnippylyContext";
import loadVelt from "../../loadSnippyly";
import { VELT_SDK_INIT_EVENT, VELT_SDK_VERSION, INTEGRITY_MAP, } from "../../constants";
import { deepCompare } from "../../utils/propUtils";
var SnippylyProvider = function (props) {
    var apiKey = props.apiKey, user = props.user, config = props.config, documentId = props.documentId, language = props.language, translations = props.translations, autoTranslation = props.autoTranslation, userDataProvider = props.userDataProvider, dataProviders = props.dataProviders, encryptionProvider = props.encryptionProvider, authProvider = props.authProvider, permissionProvider = props.permissionProvider, onClientLoad = props.onClientLoad, children = props.children;
    var _a = useState(null), client = _a[0], setClient = _a[1];
    var prevAuthProviderRef = useRef(undefined);
    var prevUserDataProviderRef = useRef(undefined);
    var prevDataProvidersRef = useRef(undefined);
    var prevPermissionProviderRef = useRef(undefined);
    useEffect(function () {
        if (apiKey) {
            var staging = config === null || config === void 0 ? void 0 : config.staging;
            var develop = config === null || config === void 0 ? void 0 : config.develop;
            var version = (config === null || config === void 0 ? void 0 : config.version) || VELT_SDK_VERSION;
            var integrity = !!(config === null || config === void 0 ? void 0 : config.integrity);
            var integrityValue = "";
            if (integrity) {
                if (develop || staging) {
                    if (config === null || config === void 0 ? void 0 : config.sriv) {
                        integrityValue = config === null || config === void 0 ? void 0 : config.sriv;
                    }
                }
                else {
                    if (INTEGRITY_MAP[version]) {
                        integrityValue = INTEGRITY_MAP[version];
                    }
                }
            }
            loadVelt(function () {
                initVelt();
            }, version, staging, develop, config === null || config === void 0 ? void 0 : config.proxyDomain, integrity, integrityValue);
        }
    }, []);
    useEffect(function () {
        if (!deepCompare(prevUserDataProviderRef.current, userDataProvider)) {
            if (client && userDataProvider) {
                if (typeof (client === null || client === void 0 ? void 0 : client.setUserDataProvider) === "function") {
                    client === null || client === void 0 ? void 0 : client.setUserDataProvider(userDataProvider);
                }
                prevUserDataProviderRef.current = userDataProvider;
            }
            else {
                // Only update ref to undefined if client is available (to track removal)
                // If client is not available, keep old ref so we can set provider when client becomes available
                if (client) {
                    prevUserDataProviderRef.current = userDataProvider;
                }
            }
        }
    }, [client, userDataProvider]);
    useEffect(function () {
        if (!deepCompare(prevDataProvidersRef.current, dataProviders)) {
            if (client && dataProviders) {
                if (typeof (client === null || client === void 0 ? void 0 : client.setDataProviders) === "function") {
                    client === null || client === void 0 ? void 0 : client.setDataProviders(dataProviders);
                }
                prevDataProvidersRef.current = dataProviders;
            }
            else {
                // Only update ref to undefined if client is available (to track removal)
                // If client is not available, keep old ref so we can set provider when client becomes available
                if (client) {
                    prevDataProvidersRef.current = dataProviders;
                }
            }
        }
    }, [client, dataProviders]);
    useEffect(function () {
        if (client && encryptionProvider) {
            if (typeof (client === null || client === void 0 ? void 0 : client.setEncryptionProvider) === "function") {
                client === null || client === void 0 ? void 0 : client.setEncryptionProvider(encryptionProvider);
            }
        }
    }, [client, encryptionProvider]);
    useEffect(function () {
        if (!deepCompare(prevAuthProviderRef.current, authProvider)) {
            if (client && authProvider && authProvider.user) {
                client.setVeltAuthProvider(authProvider);
                prevAuthProviderRef.current = authProvider;
            }
            else {
                // Only update ref to undefined if client is available (to track removal)
                // If client is not available, keep old ref so we can set provider when client becomes available
                if (client) {
                    prevAuthProviderRef.current = authProvider;
                }
            }
        }
    }, [client, authProvider]);
    useEffect(function () {
        if (!deepCompare(prevPermissionProviderRef.current, permissionProvider)) {
            if (client && permissionProvider) {
                if (typeof (client === null || client === void 0 ? void 0 : client.setPermissionProvider) === "function") {
                    client === null || client === void 0 ? void 0 : client.setPermissionProvider(permissionProvider);
                }
                prevPermissionProviderRef.current = permissionProvider;
            }
            else {
                // Only update ref to undefined if client is available (to track removal)
                // If client is not available, keep old ref so we can set provider when client becomes available
                if (client) {
                    prevPermissionProviderRef.current = permissionProvider;
                }
            }
        }
    }, [client, permissionProvider]);
    var initVelt = function () { return __awaiter(void 0, void 0, void 0, function () {
        var velt, event;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    if (!config) return [3 /*break*/, 2];
                    if (config.staging) {
                        delete config.staging;
                    }
                    if (config.develop) {
                        delete config.develop;
                    }
                    if (config === null || config === void 0 ? void 0 : config.version) {
                        config === null || config === void 0 ? true : delete config.version;
                    }
                    if (config === null || config === void 0 ? void 0 : config.proxyDomain) {
                        delete config.proxyDomain;
                    }
                    return [4 /*yield*/, ((_a = window.Velt) === null || _a === void 0 ? void 0 : _a.init(apiKey, config))];
                case 1:
                    velt = _c.sent();
                    return [3 /*break*/, 4];
                case 2: return [4 /*yield*/, ((_b = window.Velt) === null || _b === void 0 ? void 0 : _b.init(apiKey))];
                case 3:
                    velt = _c.sent();
                    _c.label = 4;
                case 4:
                    // Set language
                    if (language && (velt === null || velt === void 0 ? void 0 : velt.setLanguage)) {
                        velt === null || velt === void 0 ? void 0 : velt.setLanguage(language);
                    }
                    // Set translations
                    if (translations && (velt === null || velt === void 0 ? void 0 : velt.setTranslations)) {
                        if (typeof translations === "object") {
                            Object.keys(translations).forEach(function (languageCode) {
                                velt === null || velt === void 0 ? void 0 : velt.setTranslations(languageCode, translations[languageCode] || {});
                            });
                        }
                    }
                    if ([true, false].includes(autoTranslation)) {
                        if (autoTranslation && (velt === null || velt === void 0 ? void 0 : velt.enableAutoTranslation)) {
                            velt === null || velt === void 0 ? void 0 : velt.enableAutoTranslation();
                        }
                        else if (!autoTranslation && (velt === null || velt === void 0 ? void 0 : velt.disableAutoTranslation)) {
                            velt === null || velt === void 0 ? void 0 : velt.disableAutoTranslation();
                        }
                    }
                    if (velt === null || velt === void 0 ? void 0 : velt.st) {
                        velt === null || velt === void 0 ? void 0 : velt.st("react");
                    }
                    setClient(velt);
                    if (onClientLoad) {
                        onClientLoad(velt);
                    }
                    if (!user) return [3 /*break*/, 6];
                    return [4 /*yield*/, (velt === null || velt === void 0 ? void 0 : velt.identify(user))];
                case 5:
                    _c.sent();
                    _c.label = 6;
                case 6:
                    if (documentId) {
                        velt === null || velt === void 0 ? void 0 : velt.setDocumentId(documentId);
                    }
                    event = new CustomEvent(VELT_SDK_INIT_EVENT, { detail: velt });
                    window.dispatchEvent(event);
                    return [2 /*return*/];
            }
        });
    }); };
    return (React.createElement(React.Fragment, null,
        React.createElement(VeltContext.Provider, { value: { client: client } }, children)));
};
export default SnippylyProvider;
